import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ACCOUNT_SCHOOL_PROFILE_PATH } from './accountPaths';
import type { SchoolPayload, StudentPayload } from './checkInTypes';
import StudentsPanel from './StudentsPanel';

const AccountStudentsSection = () => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [school, setSchool] = useState<SchoolPayload | null>(null);
  const [students, setStudents] = useState<StudentPayload[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const schoolRes = await fetch('/api/school', { credentials: 'include' });
      if (!schoolRes.ok) {
        throw new Error(`Failed to load school (${schoolRes.status})`);
      }
      const schoolData = (await schoolRes.json()) as { school: SchoolPayload | null };
      setSchool(schoolData.school);

      if (!schoolData.school) {
        setStudents([]);
        setStatus('ready');
        return;
      }

      const studentsRes = await fetch('/api/students', { credentials: 'include' });
      if (!studentsRes.ok) {
        throw new Error(`Failed to load students (${studentsRes.status})`);
      }
      const studentsData = (await studentsRes.json()) as { students: StudentPayload[] };
      setStudents(studentsData.students);
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === 'loading') {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <p className="font-sans text-sm text-ink/60">Loading students…</p>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <h2 className="font-display text-xl font-semibold text-ink">Students</h2>
        <p className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink" role="alert">
          {error}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
        >
          Try again
        </button>
      </section>
    );
  }

  if (!school) {
    return (
      <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
        <h2 className="font-display text-xl font-semibold text-ink">Students</h2>
        <p className="mt-2 font-sans text-sm text-ink/65">
          Create a school profile first, then come back to manage students.
        </p>
        <Link
          to={ACCOUNT_SCHOOL_PROFILE_PATH}
          className="mt-5 inline-flex rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft"
        >
          Go to school profile
        </Link>
      </section>
    );
  }

  return (
    <section className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
      <StudentsPanel
        school={school}
        students={students}
        onStudentsChange={setStudents}
      />
    </section>
  );
};

export default AccountStudentsSection;
